import { el, clear, qs } from "../../utils/dom";
import { getState, setState } from "../../state/store";
import { toast } from "./Toasts";
import type { ClassData, Group } from "../../types/models";
import { chooseColor } from "../../utils/colors";
import { pushHistory } from "../../state/history";
import { createAvatarElement } from "../../utils/avatar";

export interface GroupRenderOptions {
    showControls?: boolean;
    enableDragDrop?: boolean;
    constraintMode?: boolean;
}

/**
 * Render groups into a container with optional controls and interactivity
 */
export function renderGroups(
    container: HTMLElement,
    cls: ClassData,
    options: GroupRenderOptions = { showControls: true, enableDragDrop: true }
): void {
    clear(container);

    if (!cls.lastGroups.length) {
        container.append(el("div", {}, "No groups yet. Click Generate."));
        return;
    }

    // Add control buttons if enabled
    if (options.showControls) {
        const controls = el("div", { style: "margin-bottom: 16px; display: flex; gap: 8px; flex-wrap: wrap; align-items: center;" });

        const addGroupBtn = el("button", { class: "primary" }, "+ Add Group");
        addGroupBtn.addEventListener("click", () => {
            // Save history before modification
            pushHistory(cls.id, "Added group");

            setState(ns => {
                const c = ns.classes.find(x => x.id === cls.id)!;
                const nextId = Math.max(0, ...c.lastGroups.map(g => parseInt(g.id.replace(/^g/, "")) || 0)) + 1;
                c.lastGroups.push({
                    id: `g${nextId}`,
                    memberIds: [],
                    color: chooseColor(c.lastGroups.length)
                });
            });
            document.dispatchEvent(new CustomEvent("groups:update"));
        });

        const removeEmptyBtn = el("button", {}, "Remove Empty Groups");
        removeEmptyBtn.addEventListener("click", () => {
            // Save history before modification
            pushHistory(cls.id, "Removed empty groups");

            setState(ns => {
                const c = ns.classes.find(x => x.id === cls.id)!;
                c.lastGroups = c.lastGroups.filter(g => g.memberIds.length > 0);
            });
            document.dispatchEvent(new CustomEvent("groups:update"));
        });

        // Constraint mode toggle
        const constraintModeBtn = el("button", {
            style: `
                margin-left: auto;
                background: ${options.constraintMode ? 'var(--primary)' : 'var(--bg)'};
                color: ${options.constraintMode ? 'white' : 'var(--fg)'};
                border: 1px solid ${options.constraintMode ? 'var(--primary)' : 'var(--border)'};
            `,
            title: "Toggle constraint creation mode - drag students onto each other to create constraints"
        }, options.constraintMode ? "🔗 Constraint Mode ON" : "🔗 Constraint Mode");

        constraintModeBtn.addEventListener("click", () => {
            constraintMode = !constraintMode;
            document.dispatchEvent(new CustomEvent("groups:update"));
        });

        // Help text for constraint mode
        if (options.constraintMode) {
            const helpText = el("div", {
                style: "width: 100%; font-size: 12px; color: var(--muted); margin-top: 8px; padding: 8px; background: var(--bg); border-radius: 6px;"
            }, "Drag student → student: Must be together | Hold Shift + drag: Must be apart");
            controls.append(addGroupBtn, removeEmptyBtn, constraintModeBtn, helpText);
        } else {
            controls.append(addGroupBtn, removeEmptyBtn, constraintModeBtn);
        }
        container.append(controls);
    }

    // Render group cards
    for (const g of cls.lastGroups) {
        const card = renderGroupCard(g, cls, options);
        container.append(card);
    }
}

/**
 * Render a single group card
 */
function renderGroupCard(
    g: Group,
    cls: ClassData,
    options: GroupRenderOptions
): HTMLElement {
    const card = el("div", { class: "group-card", style: `background: ${g.color ?? "transparent"}` });

    // Editable group name
    const defaultName = `Group ${g.id.replace(/^g/, "")}`;
    const displayName = g.name || defaultName;

    const nameEl = el("div", {
        class: "group-name",
        contenteditable: "true",
        title: "Click to edit group name",
        style: "cursor: text; outline: none; min-width: 50px; border-bottom: 1px dashed transparent;"
    }, displayName) as HTMLElement;

    nameEl.addEventListener("focus", () => {
        nameEl.style.borderBottomColor = "var(--primary)";
    });

    nameEl.addEventListener("blur", () => {
        nameEl.style.borderBottomColor = "transparent";
        const newName = nameEl.textContent?.trim() || "";
        setState(ns => {
            const c = ns.classes.find(x => x.id === cls.id);
            const group = c?.lastGroups.find(x => x.id === g.id);
            if (group) {
                // Store custom name, or delete if it matches default
                if (newName === defaultName || newName === "") {
                    delete group.name;
                } else {
                    group.name = newName;
                }
            }
        });
        // Reset to default if empty
        if (!newName) nameEl.textContent = defaultName;
    });

    nameEl.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            nameEl.blur();
        }
    });

    const head = el("div", { class: "group-head" }, nameEl);

    const list = el("div", { class: "list" });
    for (const sid of g.memberIds) {
        const st = cls.students.find(s => s.id === sid);
        if (!st) continue;

        const isLocked = st.lockedToGroupId === g.id;

        const itemAttrs: Record<string, string | boolean> = {
            class: `student ${isLocked ? 'locked' : ''}`,
            "data-student": sid,
            style: isLocked ? "background: rgba(255,215,0,0.15);" : ""
        };
        if (options.enableDragDrop && !isLocked) {
            itemAttrs.draggable = true;
        }
        const item = el("div", itemAttrs);

        // Lock toggle button
        const lockBtn = el("button", {
            class: "lock-btn",
            title: isLocked ? "Unlock student" : "Lock to this group",
            style: `
                background: none;
                border: none;
                cursor: pointer;
                padding: 2px 4px;
                font-size: 11px;
                opacity: ${isLocked ? '1' : '0.3'};
                margin-right: 4px;
                transition: opacity 0.2s;
            `
        }, isLocked ? "🔒" : "🔓");

        lockBtn.addEventListener("mouseenter", () => {
            if (!isLocked) lockBtn.style.opacity = "0.7";
        });
        lockBtn.addEventListener("mouseleave", () => {
            if (!isLocked) lockBtn.style.opacity = "0.3";
        });

        lockBtn.addEventListener("click", (e) => {
            e.stopPropagation();
            pushHistory(cls.id, isLocked ? "Unlocked student" : "Locked student");
            setState(ns => {
                const c = ns.classes.find(x => x.id === cls.id);
                const student = c?.students.find(x => x.id === sid);
                if (student) {
                    if (isLocked) {
                        delete student.lockedToGroupId;
                    } else {
                        student.lockedToGroupId = g.id;
                    }
                }
            });
            document.dispatchEvent(new CustomEvent("groups:update"));
        });

        // Avatar element
        const avatarEl = createAvatarElement(st, 24);
        avatarEl.style.marginRight = "6px";

        const nameSpan = el("span", {}, st.name);
        item.append(lockBtn, avatarEl, nameSpan);

        // Add drag-drop listeners if enabled and not locked
        if (options.enableDragDrop && !isLocked) {
            item.addEventListener("dragstart", ev => {
                ev.dataTransfer?.setData("text/plain", sid);
                ev.dataTransfer?.setData("constraint-mode", options.constraintMode ? "true" : "false");
                // Track shift key state at drag start
                ev.dataTransfer?.setData("shift-held", ev.shiftKey ? "true" : "false");
                item.classList.add("dnd-ghost");
            });
            item.addEventListener("dragend", () => item.classList.remove("dnd-ghost"));
        }

        // In constraint mode, students can receive drops to create constraints
        if (options.constraintMode) {
            item.addEventListener("dragover", (ev) => {
                const draggedId = ev.dataTransfer?.types.includes("text/plain");
                if (draggedId) {
                    ev.preventDefault();
                    // Visual feedback
                    if (ev.shiftKey) {
                        item.style.outline = "2px solid var(--danger, #e74c3c)";
                        item.style.outlineOffset = "2px";
                    } else {
                        item.style.outline = "2px solid var(--success, #27ae60)";
                        item.style.outlineOffset = "2px";
                    }
                }
            });

            item.addEventListener("dragleave", () => {
                item.style.outline = "";
                item.style.outlineOffset = "";
            });

            item.addEventListener("drop", (ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                item.style.outline = "";
                item.style.outlineOffset = "";

                const draggedId = ev.dataTransfer?.getData("text/plain");
                const isConstraintDrag = ev.dataTransfer?.getData("constraint-mode") === "true";

                if (!draggedId || draggedId === sid || !isConstraintDrag) return;

                const apart = ev.shiftKey || ev.dataTransfer?.getData("shift-held") === "true";

                // Create constraint
                setState(ns => {
                    const c = ns.classes.find(x => x.id === cls.id);
                    if (!c) return;

                    const targetArray = apart ? c.constraints.mustApart : c.constraints.mustTogether;

                    // Check if a constraint set already contains either student
                    let existingSet = targetArray.find(set =>
                        set.ids.includes(draggedId) || set.ids.includes(sid)
                    );

                    if (existingSet) {
                        // Add both students to the existing set
                        if (!existingSet.ids.includes(draggedId)) existingSet.ids.push(draggedId);
                        if (!existingSet.ids.includes(sid)) existingSet.ids.push(sid);
                    } else {
                        // Create a new constraint set
                        targetArray.push({ ids: [draggedId, sid] });
                    }
                });

                const draggedStudent = cls.students.find(s => s.id === draggedId);
                const targetStudent = cls.students.find(s => s.id === sid);
                const constraintType = apart ? "Must be apart" : "Must be together";
                toast(`${constraintType}: ${draggedStudent?.name} & ${targetStudent?.name}`);

                document.dispatchEvent(new CustomEvent("state:update"));
            });
        }

        list.appendChild(item);
    }

    // Add drop listener if drag-drop enabled (only in move mode, not constraint mode)
    if (options.enableDragDrop && !options.constraintMode) {
        card.addEventListener("dragover", ev => ev.preventDefault());
        card.addEventListener("drop", ev => {
            const sid = ev.dataTransfer?.getData("text/plain");
            if (!sid) return;

            // Skip if this was a constraint mode drag
            const isConstraintDrag = ev.dataTransfer?.getData("constraint-mode") === "true";
            if (isConstraintDrag) return;

            const from = cls.lastGroups.find(gr => gr.memberIds.includes(sid));
            if (!from) return;
            if (from.id === g.id) return; // Don't save history for same-group drops

            // Save history before modification
            pushHistory(cls.id, "Moved student");

            // move
            setState(ns => {
                const c = ns.classes.find(x => x.id === cls.id)!;
                const Gto = c.lastGroups.find(x => x.id === g.id)!;
                const Gfrom = c.lastGroups.find(x => x.id === from.id)!;
                if (!Gfrom || !Gto) return;
                if (Gfrom.id === Gto.id) return;

                // Validate student is actually in source group
                if (!Gfrom.memberIds.includes(sid)) return;

                // naive hard-rule check omitted here for brevity; generator enforces on next build
                Gfrom.memberIds = Gfrom.memberIds.filter(x => x !== sid);
                Gto.memberIds.push(sid);
            });
            document.dispatchEvent(new CustomEvent("groups:update"));
        });
    }

    card.append(head, list);
    return card;
}

// Global state for constraint mode
let constraintMode = false;

export function mountGroupsBoard() {
    const host = qs<HTMLElement>("#groups-board");
    render();

    function render() {
        const s = getState();
        const cls = s.classes.find(c => c.id === s.activeClassId);
        if (!cls) {
            clear(host);
            host.append(el("div", {}, "No class."));
            return;
        }

        renderGroups(host, cls, { showControls: true, enableDragDrop: true, constraintMode });
    }

    document.addEventListener("groups:update", render);
    document.addEventListener("state:update", render);
}

/**
 * Toggle constraint mode on/off
 */
export function toggleConstraintMode(): boolean {
    constraintMode = !constraintMode;
    document.dispatchEvent(new CustomEvent("groups:update"));
    return constraintMode;
}

/**
 * Check if constraint mode is active
 */
export function isConstraintModeActive(): boolean {
    return constraintMode;
}
